import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './BrandManagement.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandManagement = () => {
  const navigate = useNavigate();
  const { brandId } = useParams();
  const { sessionToken } = useAuth();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [branches, setBranches] = useState([]);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBrands();
    if (brandId) {
      loadBrandDetails(brandId);
    }
  }, [brandId]);

  const loadBrands = async () => {
    try {
      const response = await axios.get(`${API}/merchant/brands`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success) {
        setBrands(response.data.brands);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBrandDetails = async (id) => {
    try {
      const brand = brands.find(b => b.id === id);
      if (brand) {
        setSelectedBrand(brand);
      }
      
      const response = await axios.get(`${API}/merchant/brands/${id}/branches`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error loading brand details:', error);
    }
  };

  const handleCreateBrand = async (formData) => {
    try {
      const response = await axios.post(
        `${API}/merchant/brands`,
        formData,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.success) {
        setShowBrandModal(false);
        loadBrands();
        alert('Brand created successfully!');
      }
    } catch (error) {
      console.error('Error creating brand:', error);
      alert('Failed to create brand');
    }
  };

  const handleCreateBranch = async (formData) => {
    try {
      const response = await axios.post(
        `${API}/merchant/brands/${selectedBrand.id}/branches`,
        formData,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (response.data.success) {
        setShowBranchModal(false);
        loadBrandDetails(selectedBrand.id);
        alert('Branch created successfully!');
      }
    } catch (error) {
      console.error('Error creating branch:', error);
      alert('Failed to create branch');
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading brands...</div></Layout>;
  }

  if (selectedBrand) {
    return (
      <Layout>
        <div className="brand-management">
          <div className="page-header">
            <button className="back-btn" onClick={() => { setSelectedBrand(null); navigate('/merchant/brands'); }}>
              ← Back to Brands
            </button>
            <h1>{selectedBrand.name}</h1>
            <button className="btn-primary" onClick={() => navigate('/merchant/products')}>
              Manage Products
            </button>
          </div>

          <div className="brand-details-card">
            <h2>Brand Information</h2>
            <div className="details-grid">
              <div><strong>Location:</strong> {selectedBrand.city}, {selectedBrand.country}</div>
              <div><strong>Address:</strong> {selectedBrand.address || 'N/A'}</div>
              <div><strong>Website:</strong> {selectedBrand.website || 'N/A'}</div>
              <div><strong>Products:</strong> {selectedBrand.product_count || 0}</div>
            </div>
          </div>

          <div className="branches-section">
            <div className="section-header">
              <h2>🏢 Branches</h2>
              <button className="btn-primary" onClick={() => setShowBranchModal(true)}>
                + Add Branch
              </button>
            </div>
            
            {branches.length === 0 ? (
              <div className="empty-state">
                <p>No branches yet. Add your first branch location.</p>
              </div>
            ) : (
              <div className="branches-grid">
                {branches.map(branch => (
                  <div key={branch.id} className="branch-card">
                    <h3>{branch.name}</h3>
                    <p>📍 {branch.address}</p>
                    <p>📞 {branch.phone}</p>
                    {branch.manager_name && <p>👤 Manager: {branch.manager_name}</p>}
                    {branch.operating_hours && <p>🕐 {branch.operating_hours}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {showBranchModal && (
            <BranchModal
              onClose={() => setShowBranchModal(false)}
              onSave={handleCreateBranch}
            />
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="brand-management">
        <div className="page-header">
          <h1>🏪 Brand Management</h1>
          <button className="btn-primary" onClick={() => setShowBrandModal(true)}>
            + Create Brand
          </button>
        </div>

        {brands.length === 0 ? (
          <div className="empty-state-large">
            <div className="empty-icon">🏪</div>
            <h2>No Brands Yet</h2>
            <p>Create your first brand to start selling products</p>
            <button className="btn-primary" onClick={() => setShowBrandModal(true)}>
              Create Your First Brand
            </button>
          </div>
        ) : (
          <div className="brands-list">
            {brands.map(brand => (
              <div key={brand.id} className="brand-list-card">
                <div className="brand-info">
                  <h2>{brand.name}</h2>
                  <p>{brand.description || 'No description'}</p>
                  <div className="brand-meta">
                    <span>📍 {brand.city}, {brand.country}</span>
                    <span>📦 {brand.product_count || 0} products</span>
                  </div>
                </div>
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setSelectedBrand(brand);
                    navigate(`/merchant/brands/${brand.id}`);
                  }}
                >
                  Manage →
                </button>
              </div>
            ))}
          </div>
        )}

        {showBrandModal && (
          <BrandModal
            onClose={() => setShowBrandModal(false)}
            onSave={handleCreateBrand}
          />
        )}
      </div>
    </Layout>
  );
};

const BrandModal = ({ onClose, onSave, brand = null }) => {
  const [formData, setFormData] = useState({
    name: brand?.name || '',
    description: brand?.description || '',
    country: brand?.country || '',
    city: brand?.city || '',
    address: brand?.address || '',
    website: brand?.website || '',
    instagram: brand?.instagram || '',
    facebook: brand?.facebook || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <h2>{brand ? 'Edit Brand' : 'Create New Brand'}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Brand Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Country *</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              placeholder="https://..."
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Instagram</label>
              <input
                type="url"
                value={formData.instagram}
                onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                placeholder="https://instagram.com/..."
              />
            </div>
            <div className="form-group">
              <label>Facebook</label>
              <input
                type="url"
                value={formData.facebook}
                onChange={(e) => setFormData({...formData, facebook: e.target.value})}
                placeholder="https://facebook.com/..."
              />
            </div>
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {brand ? 'Update Brand' : 'Create Brand'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BranchModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    manager_name: '',
    operating_hours: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Add New Branch</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Branch Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              placeholder="Downtown Store"
            />
          </div>
          
          <div className="form-group">
            <label>Address *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Manager Name</label>
            <input
              type="text"
              value={formData.manager_name}
              onChange={(e) => setFormData({...formData, manager_name: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Operating Hours</label>
            <input
              type="text"
              value={formData.operating_hours}
              onChange={(e) => setFormData({...formData, operating_hours: e.target.value})}
              placeholder="Mon-Fri: 9AM-6PM"
            />
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Add Branch
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BrandManagement;